import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function ServicesFeatures() {
  const { ref: offRef, isVisible: offVis } = useScrollAnimation({ threshold: 0.15 });
  const { ref: studioRef, isVisible: studioVis } = useScrollAnimation({ threshold: 0.15 });
  const { ref: neuRef, isVisible: neuVis } = useScrollAnimation({ threshold: 0.15 });

  const offSiteServices = [
    { icon: 'ri-music-2-line', label: 'Live Band and Entertainment' },
    { icon: 'ri-piano-line', label: 'Private Piano Performances' },
    { icon: 'ri-camera-2-line', label: 'Photography and Videography' },
    { icon: 'ri-sound-module-line', label: 'Audio, Visual and Technical Production' },
    { icon: 'ri-stage-line', label: 'Stage Production and Creative Support' },
    { icon: 'ri-lightbulb-line', label: 'Creative Direction and Consulting' },
  ];

  return (
    <>
      {/* Off-Site Services Spotlight */}
      <section ref={offRef} className="py-24 bg-white overflow-hidden">
        <div className="max-w-screen-xl mx-auto px-8">
          <div className={`scroll-hidden ${offVis ? 'scroll-visible' : ''} flex flex-col lg:flex-row gap-14 items-center`}>
            <div className="flex-1">
              <span className="inline-block px-4 py-1.5 rounded-full bg-emerald-50 text-emerald-700 font-body text-xs font-semibold uppercase tracking-widest mb-5">
                Events and Brands
              </span>
              <h2 className="font-heading text-5xl font-black text-brand-blue-dark mb-5 leading-tight">
                Professional Services<br /><span className="gradient-text">Available Off-Site</span>
              </h2>
              <p className="font-body text-gray-500 text-base leading-relaxed mb-8 max-w-lg">
                Creative Institute provides professional creative services for organizations, brands, and events. Our team delivers high-level execution, strategic planning, and production excellence across any environment.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
                {offSiteServices.map((s) => (
                  <div key={s.label} className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-50 text-emerald-700 flex-shrink-0">
                      <i className={`${s.icon} text-sm`} />
                    </div>
                    <p className="font-body text-gray-600 text-sm">{s.label}</p>
                  </div>
                ))}
              </div>
              <Link
                to="/off-site-services"
                className="whitespace-nowrap inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-r from-[#065F46] to-[#059669] text-white font-body font-bold text-sm hover:opacity-90 transition-opacity cursor-pointer"
              >
                View All Off-Site Services <i className="ri-arrow-right-line" />
              </Link>
            </div>

          </div>
        </div>
      </section>

      {/* Recording Studio Spotlight */}
      <section ref={studioRef} className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#0F172A]" />
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/a1e0cb2eaa95a409c8a30f29dfaba42d.jpg"
            alt="Creative Institute Recording Studio"
            className="w-full h-full object-cover object-top opacity-20"
          />
        </div>
        <div className="absolute top-0 left-0 right-0 bottom-0 bg-gradient-to-r from-[#0F172A]/80 via-transparent to-[#0F172A]/80" />

        <div className="relative z-10 max-w-screen-xl mx-auto px-8">
          <div className={`scroll-hidden ${studioVis ? 'scroll-visible' : ''} flex flex-col lg:flex-row gap-16 items-center`}>
            <div className={`scroll-hidden-left ${studioVis ? 'scroll-visible' : ''} flex-shrink-0 w-full lg:w-[420px] h-[360px] rounded-3xl overflow-hidden border-2 border-white/10`}>
              <img
                src="https://readdy.ai/api/search-image?query=close%20up%20professional%20microphone%20in%20recording%20studio%20with%20mixing%20console%20in%20background%2C%20warm%20golden%20studio%20lighting%20with%20deep%20blue%20shadows%2C%20dramatic%20bokeh%20effect%2C%20high-end%20audio%20equipment%2C%20artistic%20photography%2C%20luxury%20recording%20environment&width=420&height=360&seq=studio-close-001&orientation=portrait"
                alt="Recording studio microphone setup"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="flex-1">
              <span className="inline-block px-4 py-1.5 rounded-full border border-brand-yellow/40 text-brand-yellow font-body text-xs font-semibold uppercase tracking-widest bg-brand-yellow/10 mb-5">
                Professional Studio
              </span>
              <h2 className="font-heading text-5xl font-black text-white mb-5 leading-tight">
                Record, Mix,<br />and <span className="text-brand-yellow">Master</span>
              </h2>
              <p className="font-body text-white/70 text-base leading-relaxed mb-8 max-w-lg">
                Our professional recording studio is fully equipped for artists, creators, and productions ready to bring their sound to life at the highest level. From vocal sessions to full production, we deliver quality results.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-10">
                {[
                  { icon: 'ri-mic-line', label: 'Vocal Recording' },
                  { icon: 'ri-equalizer-line', label: 'Mixing and Mastering' },
                  { icon: 'ri-headphone-line', label: 'Full Production' },
                  { icon: 'ri-music-line', label: 'Artist Sessions' },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-3 p-4 rounded-xl bg-white/6 border border-white/10">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-brand-yellow/15 text-brand-yellow flex-shrink-0">
                      <i className={`${item.icon} text-base`} />
                    </div>
                    <p className="font-body text-white/80 text-sm font-medium">{item.label}</p>
                  </div>
                ))}
              </div>
              <Link
                to="/recording-studio"
                className="whitespace-nowrap inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-brand-yellow text-brand-blue-dark font-body font-bold text-sm hover:bg-brand-yellow-light transition-all cursor-pointer"
              >
                Explore the Studio <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Neurodivergent Development Spotlight */}
      <section ref={neuRef} className="py-24 bg-gradient-to-br from-[#FFFBEB] via-white to-[#FEF3C7] overflow-hidden">
        <div className="max-w-screen-xl mx-auto px-8">
          <div className={`scroll-hidden ${neuVis ? 'scroll-visible' : ''} max-w-5xl mx-auto text-center mb-14`}>
            <span className="inline-block px-4 py-1.5 rounded-full bg-amber-100 text-amber-700 font-body text-xs font-semibold uppercase tracking-widest mb-5">
              Inclusive by Design
            </span>
            <h2 className="font-heading text-5xl font-black text-brand-blue-dark mb-5 leading-tight">
              Neurodivergent <span className="gradient-text">Creative Development</span>
            </h2>
            <p className="font-body text-gray-600 text-base leading-relaxed max-w-2xl mx-auto">
              A dedicated program that meets neurodivergent creatives exactly where they are. Every session is thoughtfully designed to build skill, confidence, and self-expression in a structured, patient, and affirming environment.
            </p>
          </div>
          <div className={`scroll-hidden delay-200 ${neuVis ? 'scroll-visible' : ''} grid grid-cols-1 md:grid-cols-4 gap-5 max-w-4xl mx-auto mb-12`}>
            {[
              { icon: 'ri-heart-3-line', title: 'Safe and Affirming', desc: 'A judgment-free environment built on respect and inclusion' },
              { icon: 'ri-user-settings-line', title: 'Fully Individualized', desc: 'Every session adapted to the unique needs of each student' },
              { icon: 'ri-seedling-line', title: 'Skill-Based Growth', desc: 'Real progress through structured and meaningful instruction' },
              { icon: 'ri-group-line', title: 'Family Involved', desc: 'We keep families informed and part of the journey' },
            ].map((item) => (
              <div key={item.title} className="p-6 rounded-2xl bg-white border border-amber-100 card-lift text-center group hover:border-amber-200">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-amber-50 text-amber-600 mx-auto mb-4 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  <i className={`${item.icon} text-xl`} />
                </div>
                <h4 className="font-heading text-brand-blue-dark text-base font-bold mb-2">{item.title}</h4>
                <p className="font-body text-gray-400 text-xs leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className={`scroll-hidden delay-400 ${neuVis ? 'scroll-visible' : ''} text-center`}>
            <Link
              to="/neurodivergent-development"
              className="whitespace-nowrap inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-r from-[#B45309] to-[#D97706] text-white font-body font-bold text-sm hover:opacity-90 transition-opacity cursor-pointer"
            >
              Learn About This Program <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
